/**
 * The PDF export on the main thread (decisions 8, 14; part 21, table K): the book read as it is now, sent to the layout
 * worker, its progress shown, and the finished file offered to save. The worker does the work, so editing goes on while
 * a book exports (decision 14). Cancelling stops the worker's export and keeps nothing of it; the file offered is let go
 * once the writer has saved it or turned the offer down.
 *
 * The worker connection, the book's reading and the browser's object URLs are injected, so the logic runs in tests.
 */
import type { PdfBook, PdfExport } from '@cockatrice/layout';
import { createSignal } from 'solid-js';
import type { Accessor } from 'solid-js';
import type { LayoutConnection } from '../preview/controller.ts';

/** What the export shows (K-rows' outcomes). */
export type ExportState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'reading' }
  | { readonly kind: 'exporting'; readonly done: number; readonly total: number }
  | { readonly kind: 'ready'; readonly url: string; readonly name: string; readonly size: number }
  | { readonly kind: 'failed'; readonly reason: 'unreadable' | 'failed' };

/** What the controller needs; everything with an effect is injected. */
export interface ExportOptions {
  /** The layout worker, shared with the preview (`sharedWorker`). */
  readonly connect: () => LayoutConnection;
  /** The open book as the PDF takes it (table N), or null when it can't be read whole. */
  readonly readBook: () => Promise<PdfBook | null>;
  /** An object URL for the file (`URL.createObjectURL` of a PDF blob), and what lets it go. */
  readonly objectUrl: (bytes: Uint8Array<ArrayBuffer>) => string;
  readonly revokeUrl: (url: string) => void;
}

/** K12: the longest file name offered, before its extension. */
export const MAX_FILE_NAME = 100;

/**
 * K12: the file's name: the book's title without the characters file systems refuse, or "book" when none is left, with
 * `extension` (part 24a's bundle names its zip alike).
 */
export function fileName(title: string, extension = 'pdf'): string {
  const name = title
    .replaceAll(/\s/gu, ' ')
    .replaceAll(/[\\/:*?"<>|\p{Cc}\p{Cf}]/gu, '')
    .replaceAll(/ +/gu, ' ')
    .trim()
    .slice(0, MAX_FILE_NAME)
    .trim();
  return `${name === '' ? 'book' : name}.${extension}`;
}

/** The export's requests to the layout worker. */
export class ExportController {
  readonly state: Accessor<ExportState>;
  readonly #setState: (state: ExportState) => void;
  readonly #options: ExportOptions;
  /** The export under way: a new number for each, so answers for an earlier one are ignored. */
  #job = 0;
  #running: PdfExport | null = null;

  constructor(options: ExportOptions) {
    this.#options = options;
    const [state, setState] = createSignal<ExportState>({ kind: 'idle' });
    this.state = state;
    this.#setState = setState;
  }

  /** K1, K2: exports the book as it is now; nothing while one is under way. A file offered before is let go. */
  start(): void {
    const kind = this.state().kind;
    if (kind === 'reading' || kind === 'exporting') return;
    this.#forget();
    this.#job += 1;
    const job = this.#job;
    this.#setState({ kind: 'reading' });
    this.#options.readBook().then(
      (book) => {
        if (job !== this.#job) return;
        // K7: a chapter that can't be read is never left out of the book.
        if (book === null) this.#setState({ kind: 'failed', reason: 'unreadable' });
        else this.#export(job, book);
      },
      () => {
        this.#fail(job);
      },
    );
  }

  /** K4–K6: stops the export under way, keeping nothing of it; a file offered stays offered. */
  cancel(): void {
    const kind = this.state().kind;
    if (kind !== 'reading' && kind !== 'exporting') return;
    this.#job += 1;
    const running = this.#running;
    this.#running = null;
    // The worker answers the cancelled export with null; a failure to reach it changes nothing here.
    running?.cancel().catch(() => undefined);
    this.#setState({ kind: 'idle' });
  }

  /** K9: puts the offer or the failure away. */
  dismiss(): void {
    const kind = this.state().kind;
    if (kind !== 'ready' && kind !== 'failed') return;
    this.#forget();
    this.#setState({ kind: 'idle' });
  }

  /** K10: the app closing: nothing under way, nothing kept. */
  stop(): void {
    this.cancel();
    this.dismiss();
  }

  /** K1, K3, K8: sends the book; its progress and its file, while it is the export under way. */
  #export(job: number, book: PdfBook): void {
    const running = this.#options.connect().client.pdf(book, (done, total) => {
      if (job === this.#job) this.#setState({ kind: 'exporting', done, total });
    });
    this.#running = running;
    this.#setState({ kind: 'exporting', done: 0, total: book.chapters.reduce((sum, chapter) => sum + chapter.text.length, 0) });
    running.result.then(
      (file) => {
        // K5: an answer for an export cancelled or replaced since is dropped, a late file with it.
        if (job !== this.#job) return;
        // K13: a cancelled answer to an export nobody cancelled: the export didn't happen.
        if (file === null) {
          this.#fail(job);
          return;
        }
        this.#running = null;
        this.#setState({ kind: 'ready', url: this.#options.objectUrl(file), name: fileName(book.metadata.title), size: file.byteLength });
      },
      () => {
        this.#fail(job);
      },
    );
  }

  /** K8, K11: the export under way failed. */
  #fail(job: number): void {
    if (job !== this.#job) return;
    this.#running = null;
    this.#setState({ kind: 'failed', reason: 'failed' });
  }

  /** K1, K9, K10: lets go of a file offered. */
  #forget(): void {
    const state = this.state();
    if (state.kind === 'ready') this.#options.revokeUrl(state.url);
  }
}
